import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Form, Select, Spin, Tooltip, Typography, Input, InputNumber } from "antd";
import { debounce } from "lodash";

import { selectUnits } from "../../store/selector";
import { BLUE_DARK } from "../../constant/colors";
import {
  medicationFieldNames,
  ivMedicationFields,
  nonIvMedicationFields
} from "../../constant/newPatientFormFieldNames";
import DateFormItem from "./DateFormItem";
import { PhysicianFormItem } from "./PhysicianFormItem";

const TM_DISPLAY_NAME_MAX_CHARS = 30;
const MAX_NUM_OF_CYCLES = 730;

const { Option } = Select;
const { Title, Text } = Typography;

const MedicationOrderForm = ({ form, onAssistanceProgramSearch, isSearching, programs }) => {
  const { t } = useTranslation();
  const [selectedRxnormDrug, setSelectedRxnormDrug] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [apEligibleDrugs, setApEligibleDrugs] = useState([]);
  const [selectedPhysician, setSelectedPhysician] = useState({});

  const isIvMedication = useRef(true);

  const dosageUnits = useSelector(selectUnits);

  const numberRangeRule = (msg) => ({
    type: "number",
    min: 1,
    message: msg
  });

  const onAssistanceProgramSelect = (name) => {
    const selectedProgram = programs.find((p) => p.name === name);
    if (selectedProgram) {
      setSelectedProgram(selectedProgram);
      setApEligibleDrugs(selectedProgram.possibleDrugs);
    }
  };

  const onClearAssistanceProgram = () => {
    isIvMedication.current = null;
    setSelectedRxnormDrug(null);
    setSelectedProgram(null);
    setApEligibleDrugs([]);
    form.setFieldsValue({ rxnormDrug: null });
  };

  const handleSelectDrugName = (selectedDrug) => {
    const selectedRxnormDrug = selectedDrug
      ? selectedProgram.possibleDrugs.find(({ id }) => id === selectedDrug.value)
      : null;
    form.setFieldsValue({ rxnormDrug: selectedRxnormDrug });
    if (selectedRxnormDrug) {
      setSelectedRxnormDrug(selectedRxnormDrug);
      const { isInfusion } = selectedRxnormDrug;
      isIvMedication.current = isInfusion;
    } else {
      isIvMedication.current = true;
      setSelectedRxnormDrug(null);
    }
  };

  const validateDrugNameIsSelected = () => form.validateFields([medicationFieldNames.rxnormDrug]);

  const renderOption = (apDrugDisplayName) => {
    if (apDrugDisplayName.length > TM_DISPLAY_NAME_MAX_CHARS) {
      return (
        <Tooltip title={apDrugDisplayName}>
          <span>{apDrugDisplayName.substring(0, TM_DISPLAY_NAME_MAX_CHARS)}...</span>
        </Tooltip>
      );
    }
    return apDrugDisplayName;
  };

  const filterDrugsOptions = (input, option) => {
    if (typeof option?.children === "object") {
      return (option.children.props?.title ?? "").toLowerCase().indexOf(input?.toLowerCase()) >= 0;
    } else {
      return (option.children ?? "").toLowerCase().indexOf(input?.toLowerCase()) >= 0;
    }
  };

  const numberOfCyclesFeildName = isIvMedication.current
    ? ivMedicationFields.numOfCycles
    : nonIvMedicationFields.numberOfRefillsAuthorized;

  return (
    <>
      <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
        <Title
          style={{
            fontSize: "18px",
            fontWeight: "bold",
            color: BLUE_DARK
          }}
        >
          {t("new_patient.program_selection")}
        </Title>
      </Form.Item>
      <Form.Item colon={false} label={t("new_patient.program_label")} style={{ marginBottom: 0 }}>
        <Form.Item
          name="program"
          rules={[
            {
              required: true,
              message: t("new_patient.assistance_program.required_message")
            }
          ]}
          style={{ display: "inline-block", width: "calc(50% - 8px)" }}
        >
          <Select
            placeholder={t("new_patient.assistance_program_placeholder")}
            allowClear={true}
            showSearch={true}
            onSearch={debounce(onAssistanceProgramSearch, 500)}
            onSelect={onAssistanceProgramSelect}
            onClear={onClearAssistanceProgram}
            notFoundContent={isSearching ? <Spin /> : <Text>{t("new_patient.type_to_search")}</Text>}
          >
            {programs.map((p, index) => (
              <Option key={index} value={p.name}>
                {p.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name={medicationFieldNames.rxnormDrug}
          rules={[{ required: true, message: t("new_patient.rxnormDrug.required_message") }]}
          style={{
            display: "inline-block",
            width: "calc(50% - 8px)",
            margin: "0 8px"
          }}
        >
          <Select
            placeholder={t("new_patient.drug_name_placeholder")}
            labelInValue={true}
            showSearch={true}
            disabled={!selectedProgram}
            allowClear={true}
            onChange={(selectedDrug) => handleSelectDrugName(selectedDrug)}
            onClear={() => handleSelectDrugName(null)}
            filterOption={(input, option) => filterDrugsOptions(input, option)}
          >
            {apEligibleDrugs.map(({ id, tmDisplayName }) => (
              <Option key={id} value={id}>
                {renderOption(tmDisplayName)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form.Item>
      <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
        <Form.Item
          name={medicationFieldNames.dosageValue}
          rules={[
            { required: true, message: t("new_patient.dosage.required_message") },
            {
              pattern: /^\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)*$/,
              message: t("new_patient.dosage.validation_error")
            }
          ]}
          style={{ display: "inline-block", width: "calc(50% - 8px)" }}
        >
          <Input
            onFocus={validateDrugNameIsSelected}
            placeholder={t("new_patient.dosage.placeholder")}
            addonAfter={
              <Form.Item
                name={medicationFieldNames.dosageUnit}
                noStyle={true}
                rules={[{ required: true, message: t("new_patient.dosage_unit.required_message") }]}
              >
                <Select
                  options={dosageUnits.map((unit, index) => ({
                    label: unit,
                    value: unit,
                    key: index
                  }))}
                  style={{ width: "70px" }}
                />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          name={numberOfCyclesFeildName}
          rules={[
            { required: true, message: t("new_patient.num_of_cycles.required_message") },
            numberRangeRule(t("new_patient.number_of_cycles_greater_thant_zero")),
            {
              validator: async (_, value) => {
                if (value > MAX_NUM_OF_CYCLES) {
                  return Promise.reject(
                    new Error(`${t("new_patient.num_of_cycles.max_cycles_message")} ${MAX_NUM_OF_CYCLES}`)
                  );
                }

                return Promise.resolve();
              }
            }
          ]}
          style={{
            display: "inline-block",
            width: "calc(50% - 8px)",
            margin: "0 8px"
          }}
        >
          <InputNumber
            controls={false}
            min={0}
            addonBefore={t("new_patient.num_of_cycles.addon_before")}
            placeholder={t("new_patient.num_of_cycles.placeholder")}
            onFocus={validateDrugNameIsSelected}
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item label="" colon={false} style={{ marginBottom: 0 }}>
          <Form.Item
            name={isIvMedication.current ? ivMedicationFields.quantity : nonIvMedicationFields.quantityValue}
            tooltip={t("new_patient.days_supply.tooltip")}
            dependencies={[ivMedicationFields.numOfCycles, nonIvMedicationFields.numberOfRefillsAuthorized]}
            style={{ display: "inline-block", width: "calc(50% - 8px)" }}
            rules={[{ required: true, message: t("new_patient.days_supply.required_message") }]}
          >
            <InputNumber
              controls={false}
              min={0}
              placeholder={t("new_patient.days_supply.placeholder")}
              onFocus={validateDrugNameIsSelected}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name={nonIvMedicationFields.frequency}
            rules={[
              {
                required: selectedRxnormDrug && !isIvMedication.current,
                message: t("new_patient.units_per_day.required_message")
              }
            ]}
            style={{ display: "inline-block", width: "calc(50% - 8px)", margin: "0 8px" }}
          >
            <InputNumber
              disabled={selectedRxnormDrug && isIvMedication.current}
              controls={false}
              min={0}
              placeholder={t("new_patient.units_per_day.placeholder")}
              onFocus={validateDrugNameIsSelected}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form.Item>
        <Form.Item label="" colon={false} style={{ marginBottom: 0 }}>
          <Form.Item
            name={medicationFieldNames.currentCycle}
            rules={[
              {
                required: true,
                message: t("new_patient.current_cycle.required_message")
              },
              numberRangeRule(t("new_patient.current_cycle_greater_thant_zero")),
              {
                validator: async (_, value) => {
                  const numberOfCycles = form.getFieldValue(numberOfCyclesFeildName);
                  if (value !== undefined && numberOfCycles !== undefined && value > numberOfCycles) {
                    return Promise.reject(new Error(t("new_patient.current_cycle_value_error_msg")));
                  }

                  return Promise.resolve();
                }
              }
            ]}
            style={{ display: "inline-block", width: "calc(50% - 8px)" }}
          >
            <InputNumber
              controls={false}
              placeholder={t("new_patient.current_cycle_placeholder")}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <DateFormItem
            name={medicationFieldNames.nextAdminDate}
            placeholder={t("new_patient.nextAdminDate.placeholder")}
            rules={[{ required: true, message: t("new_patient.nextAdminDate.required_message") }]}
            onFocus={validateDrugNameIsSelected}
            style={{
              display: "inline-block",
              width: "calc(50% - 8px)",
              margin: "0 8px"
            }}
          />
        </Form.Item>
      </Form.Item>
      <PhysicianFormItem
        label={t("new_patient.physician_label")}
        placeholder={t("new_patient.physician.placeholder")}
        defaultValue={selectedPhysician}
        required={false}
        onPhysicianSelected={(value) => {
          setSelectedPhysician(value);
        }}
      />
    </>
  );
};

export default MedicationOrderForm;
